/**
 * PrimaryDataSet専用の詳細差分ロジック
 */

import type { PrimaryDataSet } from '../../../types/dataset.types';
import type { DataSchema, ColumnSchema, DataValue } from '../../../types/core.types';
import type { ColumnId } from '../../../types/id.types';
import { ROW_ID_COLUMN_ID } from '../../../types/system-columns';
import type {
  PrimaryDataSetDiff,
  CellChange,
  SchemaChange,
  ColumnChange,
  DiffOptions,
  ExclusionChange,
  CommentChange,
  MetadataChange,
} from './types';
import { DEFAULT_DIFF_OPTIONS } from './types';
import { hasChanges } from './diffService';

/**
 * PrimaryDataSetの詳細差分を計算
 */
export function diffPrimaryDataset(
  original: PrimaryDataSet,
  modified: PrimaryDataSet,
  options: DiffOptions = {}
): PrimaryDataSetDiff {
  const opts = { ...DEFAULT_DIFF_OPTIONS, ...options };

  const result: PrimaryDataSetDiff = {
    datasetId: original.id,
    hasDifferences: false,
    cellChanges: [],
    rowsAdded: [],
    rowsDeleted: [],
    schemaChanges: { columnChanges: [] },
    metadataChanged: false,
    metadataChanges: [],
    nameChanged: false,
    exclusionsChanged: false,
    exclusionChanges: [],
    commentsChanged: false,
    commentChanges: [],
  };

  // 名前の変更
  if (original.name !== modified.name) {
    result.nameChanged = true;
    result.oldName = original.name;
    result.newName = modified.name;
    result.hasDifferences = true;
  }

  // メタデータの変更
  result.metadataChanges = diffMetadata(original.metadata, modified.metadata);
  if (result.metadataChanges.length > 0) {
    result.metadataChanged = true;
    result.hasDifferences = true;
  }

  // 除外行の変更
  result.exclusionChanges = diffExclusions(original.exclusions, modified.exclusions);
  if (result.exclusionChanges.length > 0) {
    result.exclusionsChanged = true;
    result.hasDifferences = true;
  }

  // コメントの変更
  result.commentChanges = diffComments(original.comments, modified.comments);
  if (result.commentChanges.length > 0) {
    result.commentsChanged = true;
    result.hasDifferences = true;
  }

  // スキーマの変更
  result.schemaChanges = diffSchema(original.schema, modified.schema);
  if (result.schemaChanges.columnChanges.length > 0) {
    result.hasDifferences = true;
  }

  // 行の追加/削除を検出
  const originalRowIds = getRowIds(original);
  const modifiedRowIds = getRowIds(modified);
  const rowChanges = detectRowChanges(originalRowIds, modifiedRowIds);

  result.rowsAdded = rowChanges.added;
  result.rowsDeleted = rowChanges.removed;

  if (rowChanges.added.length > 0 || rowChanges.removed.length > 0) {
    result.hasDifferences = true;
  }

  // セル値の差分を検出（共通行のみ）
  // 列の追加/削除があった場合も、共通列のセル値は比較する
  const cellChangesResult = diffCells(original, modified, rowChanges.common, opts);
  result.cellChanges = cellChangesResult.changes;

  if (cellChangesResult.truncated) {
    result._truncated = true;
    result._truncatedCellChanges = cellChangesResult.totalChanges;
  }

  if (result.cellChanges.length > 0) {
    result.hasDifferences = true;
  }

  return result;
}

/**
 * データセットから__system_rowid__列の値を取得
 */
function getRowIds(dataset: PrimaryDataSet): number[] {
  const rowIdColumn = dataset.data[ROW_ID_COLUMN_ID];
  if (!rowIdColumn) {
    // __system_rowid__列がない場合は行インデックスを使用
    return Array.from({ length: dataset.rowCount }, (_, i) => i + 1);
  }
  return rowIdColumn.map((v) => (typeof v === 'number' ? v : parseInt(String(v), 10)));
}

/**
 * 行の追加/削除を検出
 */
export function detectRowChanges(
  originalRowIds: number[],
  modifiedRowIds: number[]
): { added: number[]; removed: number[]; common: number[] } {
  const originalSet = new Set(originalRowIds);
  const modifiedSet = new Set(modifiedRowIds);

  const added: number[] = [];
  const removed: number[] = [];
  const common: number[] = [];

  // 追加された行（modifiedにあってoriginalにない）
  for (const rowId of modifiedRowIds) {
    if (!originalSet.has(rowId)) {
      added.push(rowId);
    }
  }

  // 削除された行（originalにあってmodifiedにない）
  for (const rowId of originalRowIds) {
    if (!modifiedSet.has(rowId)) {
      removed.push(rowId);
    }
  }

  // 共通行
  for (const rowId of originalRowIds) {
    if (modifiedSet.has(rowId)) {
      common.push(rowId);
    }
  }

  return { added, removed, common };
}

/**
 * スキーマの差分を計算
 */
export function diffSchema(original: DataSchema, modified: DataSchema): SchemaChange {
  const columnChanges: ColumnChange[] = [];

  const originalColumnsById = new Map<ColumnId, ColumnSchema>();
  const modifiedColumnsById = new Map<ColumnId, ColumnSchema>();

  for (const col of original.columns) {
    originalColumnsById.set(col.id, col);
  }
  for (const col of modified.columns) {
    modifiedColumnsById.set(col.id, col);
  }

  // 削除された列
  for (const [id, col] of originalColumnsById) {
    if (!modifiedColumnsById.has(id)) {
      columnChanges.push({
        type: 'removed',
        columnId: id,
        oldName: col.name,
      });
    }
  }

  // 追加された列
  for (const [id, col] of modifiedColumnsById) {
    if (!originalColumnsById.has(id)) {
      columnChanges.push({
        type: 'added',
        columnId: id,
        newName: col.name,
      });
    }
  }

  // 変更された列（両方に存在）
  for (const [id, originalCol] of originalColumnsById) {
    const modifiedCol = modifiedColumnsById.get(id);
    if (!modifiedCol) continue;

    // 名前変更
    if (originalCol.name !== modifiedCol.name) {
      columnChanges.push({
        type: 'renamed',
        columnId: id,
        oldName: originalCol.name,
        newName: modifiedCol.name,
      });
    }

    // 型変更
    if (originalCol.type !== modifiedCol.type) {
      columnChanges.push({
        type: 'typeChanged',
        columnId: id,
        oldType: originalCol.type,
        newType: modifiedCol.type,
      });
    }

    // 測定尺度変更
    if (hasChanges(originalCol.scale, modifiedCol.scale)) {
      columnChanges.push({
        type: 'scaleChanged',
        columnId: id,
      });
    }
  }

  return { columnChanges };
}

/**
 * セル値の差分を検出
 */
export function diffCells(
  original: PrimaryDataSet,
  modified: PrimaryDataSet,
  commonRowIds: number[],
  options: Required<DiffOptions>
): { changes: CellChange[]; truncated: boolean; totalChanges: number } {
  const changes: CellChange[] = [];
  let truncated = false;
  let totalChanges = 0;

  // 行数制限チェック
  const rowsToCompare =
    commonRowIds.length > options.maxRowsToCompare
      ? commonRowIds.slice(0, options.maxRowsToCompare)
      : commonRowIds;

  // 共通列を取得
  const originalColumnIds = new Set(Object.keys(original.data) as ColumnId[]);
  const modifiedColumnIds = new Set(Object.keys(modified.data) as ColumnId[]);
  const commonColumnIds = [...originalColumnIds].filter((id) => modifiedColumnIds.has(id));

  // 行インデックスのマッピングを作成
  const originalRowIdToIndex = createRowIdToIndexMap(original);
  const modifiedRowIdToIndex = createRowIdToIndexMap(modified);

  // 共通行の各セルを比較
  for (const rowId of rowsToCompare) {
    const originalIndex = originalRowIdToIndex.get(rowId);
    const modifiedIndex = modifiedRowIdToIndex.get(rowId);

    if (originalIndex === undefined || modifiedIndex === undefined) {
      continue;
    }

    for (const columnId of commonColumnIds) {
      // システム列はスキップ
      if (columnId === ROW_ID_COLUMN_ID) continue;

      const originalValue = original.data[columnId]?.[originalIndex];
      const modifiedValue = modified.data[columnId]?.[modifiedIndex];

      if (!valuesEqual(originalValue, modifiedValue)) {
        totalChanges++;

        if (options.truncateOnLargeChange && changes.length >= options.maxCellChanges) {
          truncated = true;
          continue;
        }

        changes.push({
          rowNumber: rowId,
          columnId,
          oldValue: originalValue,
          newValue: modifiedValue,
        });
      }
    }
  }

  return { changes, truncated, totalChanges };
}

/**
 * RowId -> 配列インデックスのマッピングを作成
 */
function createRowIdToIndexMap(dataset: PrimaryDataSet): Map<number, number> {
  const map = new Map<number, number>();
  const rowIdColumn = dataset.data[ROW_ID_COLUMN_ID];

  if (rowIdColumn) {
    for (let i = 0; i < rowIdColumn.length; i++) {
      const rowId = rowIdColumn[i];
      if (typeof rowId === 'number') {
        map.set(rowId, i);
      }
    }
  } else {
    // __system_rowid__列がない場合は1-based indexを使用
    for (let i = 0; i < dataset.rowCount; i++) {
      map.set(i + 1, i);
    }
  }

  return map;
}

/**
 * 2つの値が等しいかを判定（NaN対応）
 */
function valuesEqual(a: DataValue, b: DataValue): boolean {
  // 両方nullまたはundefined
  if (a == null && b == null) return true;

  // 片方のみnullまたはundefined
  if (a == null || b == null) return false;

  // 両方NaN
  if (typeof a === 'number' && typeof b === 'number' && isNaN(a) && isNaN(b)) {
    return true;
  }

  return a === b;
}

/**
 * メタデータの差分を計算
 */
function diffMetadata(
  original: PrimaryDataSet['metadata'],
  modified: PrimaryDataSet['metadata']
): MetadataChange[] {
  const changes: MetadataChange[] = [];
  const fields = ['originalFileName', 'description', 'sourceUrl', 'sourceType'] as const;

  for (const field of fields) {
    const oldVal = original[field];
    const newVal = modified[field];
    if (oldVal !== newVal) {
      changes.push({
        field,
        oldValue: oldVal as string | undefined,
        newValue: newVal as string | undefined,
      });
    }
  }

  return changes;
}

/**
 * 除外行の差分を計算
 */
function diffExclusions(
  original: PrimaryDataSet['exclusions'],
  modified: PrimaryDataSet['exclusions']
): ExclusionChange[] {
  const changes: ExclusionChange[] = [];

  const originalRowIds = new Set(
    original?.excludedRows ? Object.keys(original.excludedRows).map(Number) : []
  );
  const modifiedRowIds = new Set(
    modified?.excludedRows ? Object.keys(modified.excludedRows).map(Number) : []
  );

  // 新しく除外された行
  for (const rowId of modifiedRowIds) {
    if (!originalRowIds.has(rowId)) {
      changes.push({ type: 'excluded', rowNumber: rowId });
    }
  }

  // 除外解除された行
  for (const rowId of originalRowIds) {
    if (!modifiedRowIds.has(rowId)) {
      changes.push({ type: 'unexcluded', rowNumber: rowId });
    }
  }

  return changes.sort((a, b) => a.rowNumber - b.rowNumber);
}

/**
 * コメントの差分を計算
 */
function diffComments(
  original: PrimaryDataSet['comments'],
  modified: PrimaryDataSet['comments']
): CommentChange[] {
  const changes: CommentChange[] = [];

  const originalRowIds = new Set(original ? Object.keys(original).map(Number) : []);
  const modifiedRowIds = new Set(modified ? Object.keys(modified).map(Number) : []);

  // 追加されたコメント
  for (const rowId of modifiedRowIds) {
    if (!originalRowIds.has(rowId)) {
      changes.push({
        type: 'added',
        rowNumber: rowId,
        newComment: modified![rowId].comment,
      });
    }
  }

  // 削除されたコメント
  for (const rowId of originalRowIds) {
    if (!modifiedRowIds.has(rowId)) {
      changes.push({
        type: 'removed',
        rowNumber: rowId,
        oldComment: original![rowId].comment,
      });
    }
  }

  // 変更されたコメント
  for (const rowId of originalRowIds) {
    if (modifiedRowIds.has(rowId)) {
      const oldComment = original![rowId].comment;
      const newComment = modified![rowId].comment;
      if (oldComment !== newComment) {
        changes.push({
          type: 'modified',
          rowNumber: rowId,
          oldComment,
          newComment,
        });
      }
    }
  }

  return changes.sort((a, b) => a.rowNumber - b.rowNumber);
}
